import React from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, Legend, PieChart, Pie, Cell,
} from 'recharts';

const CHART_COLORS = [
  '#0F766E', '#3B82F6', '#8B5CF6', '#F59E0B', '#EF4444',
  '#06B6D4', '#EC4899', '#14B8A6',
];

const formatMoney = (n, currency = 'PEN') => {
  if (n == null || n === '') return '—';
  const symbol = currency === 'USD' ? 'US$' : 'S/';
  return `${symbol} ${Number(n).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatPrice = (n) => {
  if (n == null || n === '') return '—';
  return Number(n).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 4 });
};

function KpiCard({ label, value, sub, accent = 'border-teal-500', icon }) {
  return (
    <div className={`bg-white rounded-xl shadow-sm p-5 border-l-4 ${accent}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-sm text-gray-500 mb-1">{label}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          {sub ? <p className="text-xs text-gray-500 mt-1">{sub}</p> : null}
        </div>
        {icon ? <span className="text-2xl">{icon}</span> : null}
      </div>
    </div>
  );
}

export default function PriceTrackingDashboard({ data, meta }) {
  if (!data) return null;

  const { kpis, top_suppliers, price_spikes, spend_by_month, price_trend_series, price_trend_products } = data;

  const supplierPie = (top_suppliers || []).slice(0, 6).map((s) => ({
    name: s.supplier_name?.length > 18 ? `${s.supplier_name.slice(0, 18)}…` : s.supplier_name,
    value: s.total_spend,
  }));

  const lineKeys = (price_trend_products || []).map((p) => ({
    key: `${p.item_type === 'service' ? 's' : 'p'}_${p.item_id ?? p.product_id}`,
    name: p.product_name?.length > 20 ? `${p.product_name.slice(0, 20)}…` : p.product_name,
  }));

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          label="Gasto en compras"
          value={formatMoney(kpis?.total_spend)}
          sub={`${kpis?.purchase_lines || 0} líneas · ${kpis?.document_count || 0} documentos`}
          icon="💰"
        />
        <KpiCard
          label="Proveedores activos"
          value={kpis?.supplier_count ?? '—'}
          sub={`${kpis?.purchased_item_count || kpis?.product_count || 0} ítems distintos`}
          accent="border-blue-500"
          icon="🏢"
        />
        <KpiCard
          label="Insumos monitoreados"
          value={kpis?.insumo_count ?? '—'}
          sub="Marcados como insumo en catálogo"
          accent="border-violet-500"
          icon="📦"
        />
        <KpiCard
          label="Alertas de precio"
          value={kpis?.alerts_count ?? 0}
          sub={`${kpis?.high_alerts_count || 0} de alta severidad (≥${meta?.spike_threshold || 10}%)`}
          accent="border-amber-500"
          icon="⚠️"
        />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {spend_by_month?.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
            <h3 className="text-base font-semibold text-gray-800 mb-1">Gasto mensual en compras</h3>
            <p className="text-xs text-gray-500 mb-4">Suma de subtotales por mes de emisión</p>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={spend_by_month}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis tickFormatter={(v) => formatMoney(v).replace('S/ ', '')} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => formatMoney(v)} />
                <Bar dataKey="total_spend" fill="#0F766E" radius={[4, 4, 0, 0]} name="Gasto" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {supplierPie.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
            <h3 className="text-base font-semibold text-gray-800 mb-1">Participación por proveedor</h3>
            <p className="text-xs text-gray-500 mb-4">Top proveedores por monto comprado</p>
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={supplierPie}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={90}
                  paddingAngle={2}
                  dataKey="value"
                  nameKey="name"
                >
                  {supplierPie.map((_, i) => (
                    <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(v) => formatMoney(v)} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {price_trend_series?.length > 0 && lineKeys.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
          <h3 className="text-base font-semibold text-gray-800 mb-1">Evolución de precio unitario (top insumos)</h3>
          <p className="text-xs text-gray-500 mb-4">Promedio mensual del precio neto por unidad</p>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={price_trend_series}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis tickFormatter={(v) => formatPrice(v)} tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v) => formatPrice(v)} />
              <Legend />
              {lineKeys.map((serie, i) => (
                <Line
                  key={serie.key}
                  type="monotone"
                  dataKey={serie.key}
                  name={serie.name}
                  stroke={CHART_COLORS[i % CHART_COLORS.length]}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  connectNulls
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {(price_spikes?.length > 0 || top_suppliers?.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {price_spikes?.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
              <p className="text-sm font-semibold text-amber-900">⚠️ {price_spikes.length} alertas de precio</p>
              <p className="text-xs text-amber-800 mt-1">
                Variación ≥ {meta?.spike_threshold || 10}% — ver detalle en Alertas de precio
              </p>
            </div>
          )}
          {top_suppliers?.length > 0 && (
            <div className="bg-teal-50 border border-teal-200 rounded-xl p-5">
              <p className="text-sm font-semibold text-teal-900">
                Proveedor principal: {top_suppliers[0].supplier_name}
              </p>
              <p className="text-xs text-teal-800 mt-1">
                {top_suppliers[0].share_pct}% del gasto — ver ranking en Proveedores
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
